import { Events } from './events.service';
import { Injectable } from '@angular/core';

import { Preferences } from '@capacitor/preferences';
import { EnvService } from './env.service';

import { CapacitorHttp } from '@capacitor/core';
import { jwtDecode } from "jwt-decode";

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  // Settings that control how the auth service operates:
  // APP_TYPE
  //  mobile:  
  //    User credentials (user+pass+token) are stored locally
  //    Login screen is only shown when there are no credentials  (forced by authguard)
  //    Auth request only made during login, or api use
  //    API call failures remove token, but not creds
  //      Auth and network failues can/should result in visible error/warnings
  //  web:
  //    Token is stored locally
  //    Login screen shown when there is no token, or it has expired (forced by authguard)
  //    API Auth failures result in removal of token, and thus a login screen will be shown
  //    Network failues should result in visible error/warnings
  // TOKEN_TIMEOUT - lifetime of token in ms
  APP_TYPE: string = 'mobile';
  TOKEN_TIMEOUT: number = 86400000;

  // Variables that can be used to control the app...
  // true if the user is logged in; i.e. we have a valid token
  isLoggedIn:boolean = false;

  // true if we have the user+password details - will only be true if APP_TYPE=='mobile' and we actually have them!
  gotUser:boolean = false;

  // count of the failed logins
  failedLogins: number = 0;

  // count of failed login attempts (including network issues)
  softfailedLogins: number = 0;

  // The credentials object
  // { email, password, token, tokenTimestamp }
  credentials: any = { token: null, timestamp: 0 };


  constructor(private env: EnvService, private events:Events) 
  { 
    console.log("auth service");
  }

	

  // Login - called from Login page and any API calls that need to...
  login(email: String, password: String) 
  {
    // POST to the authentication endpoint, with the email and password - using the local strategy
    const httpOptions = {
      method: 'POST',
      url: this.env.API_URL + '/authentication',
      headers: { 'Content-Type':  'application/json', },
      data: {strategy: 'local', email: email, password: password}
    }
		
    return new Promise(async (resolve, reject) =>
    {
      await CapacitorHttp.request(httpOptions).then((res:any) => 
      {
        if (res && res.data)
        {
          if (res.data.accessToken)
          {
            // We have an access tolen from feathersjs; timestamp it and then create and save the credentials object.
            this.credentials = { 'token': res.data.accessToken, 'timestamp': (new Date()).getTime() };
            if (this.APP_TYPE == 'mobile') 
            { 
              this.credentials['email'] = email; 
              this.credentials['password'] = password;
            }
            this.saveCredentials(this.credentials).then(() => 
            {
              console.log('Token Stored');
            },
              error => console.error('Error storing item', error)
            );
            this.gotUser = true;
            this.isLoggedIn = true;

		
            resolve(res.data.accessToken);
          }
          else if (res.data.message)
          {
            console.log(res.data.message);
            reject(res.data.message)
          }

        }
      },(err) => { reject(err); });
    });
	
  }

  // Called to check token etc, and carry out a login if needed
  checkLogin()
  {
    return new Promise(async (resolve,reject) => 
    {
      // Check token expirey
      if (this.credentials.token)
      {
        if ((this.credentials.timestamp + this.TOKEN_TIMEOUT) < (new Date()).getTime()) 
        {
          console.log("Invalidating expired token");
          this.credentials.token = null;
          this.credentials.timestamp = 0;
          this.isLoggedIn = false;
        }
      }

      // Resolve with the token if all ok
      if (this.credentials.token && this.isLoggedIn) 
      { 
        resolve(this.credentials.token); 
      }
      else
      {
        // Get the credentials and login
        this.getCredentials().then((cred: any) => 
        {
//          console.log('user:'+JSON.stringify(user));
          this.login(cred.email, cred.password).then((res) => { resolve(res); }, (err) => { reject(err); });
        }, (err) => { this.gotUser = false; reject(err); });
      }
      
    });
  }


  register(fullName: String, email: String, password: String) 
  {
    const httpOptions = {
      method: 'POST',
      url: this.env.API_URL + '/users',
      headers: { 'Content-Type':  'application/json', },
      data: {email: email, password: password}
    }

    return new Promise(async (resolve, reject) =>
    {
      await CapacitorHttp.request(httpOptions).then((res:any) => 
      {
        if (res && res.data)
        {
          resolve(null);
        }
        else
        {
          reject();
        }
      },(err) => { reject(err); });
    });
    
      
  }


  logout() 
  {
	
    return new Promise(async (resolve, reject) =>
    { 
      this.removeCredentials();
      this.gotUser = false;
      this.isLoggedIn = false;
      this.credentials = { token: null, timestamp: 0 };
      resolve(true);
    });

  }


  getCredentials()
  {
    return new Promise(async (resolve, reject) =>
    {
      Preferences.get( { key: this.env.APP_NAME+'-credentials' }).then((data) => 
      {
        var cred = JSON.parse(data.value);
        if (cred) 
        { 
          this.credentials = cred;
          if (this.APP_TYPE == 'mobile' && cred.email && cred.password) { this.gotUser = true; }
          resolve(cred); 
        }
      }, (err) => 
      {
        reject(err);
      });
    });
  }

  saveCredentials(data)
  {
    return Preferences.set( { key: this.env.APP_NAME+'-credentials', value: JSON.stringify(data) });
  }

  removeCredentials()
  {
    return Preferences.remove({key: this.env.APP_NAME+'-credentials'});
  }
/*
  user() {

    const httpOptions = {
      method: 'POST',
      url: this.env.API_URL + '/authentication',
      headers: { 'Content-Type':  'application/json', 
                 'Authorization': this.token["token_type"]+" "+this.token["access_token"]
      }
    }
  
    return CapacitorHttp.request(httpOptions).then((res) => 

    return this.http.get<User>(this.env.API_URL + 'auth/user', { headers: headers })
    .pipe(
      tap(user => {
        return user;
      })
    )
  }
*/

  // This is called on app initialisation - usually from app.component.ts
  // The user details might be stored in local storage 
  // If we have a token, we 
  getToken() 
  {
  
    return this.getCredentials().then((data:any) => 
    {
      if (data && data.token)
      {
        this.isLoggedIn=true;
      } else {
        this.isLoggedIn=false;
        this.credentials.token = null;
        this.credentials.timestamp = 0;
      }
    },
    error => {
      this.credentials.token = null;
      this.credentials.timestamp = 0;
      this.isLoggedIn=false;
    });
  }

  // Logout if token has expired
  checkToken(token)
  {
    if (!token) { return false; }
    try {
      var decoded:any = jwtDecode(token);
      var dt = new Date().getTime();
      var exp = (decoded.exp * 1000) - dt;
      console.log(dt,decoded,exp);
      if (exp > 0)  { return true; }
    }
    catch (e)
    {
      console.log(e);
      return false;
    }
    return false;
  }

}
